import type { AdminReportDefectDto } from '@carcheck/shared';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { DefectSeverityBadge } from './DefectSeverityBadge';
import { tokens } from '../../../design';

/**
 * Inspection defects section for the report moderation drawer (AUT-151).
 *
 * Renders the report's `defects[]` (newest-first per the wire DTO) inside
 * the Details tab, mirroring the mockup `design/mockups/inspection-review.html`
 * «Отмеченные дефекты» block: one card, one row per defect carrying a
 * severity badge + category label + free-text note. When a defect is
 * tagged on a media item (`mediaId !== null`) a small "фото" chip cues
 * the operator that the defect ties to a specific shot in the Media tab.
 *
 * AUT-340 — a defect placed on the body carries its ZONE as a chip next
 * to the category, because «Скол на крыле» does not say WHICH wing and
 * the operator judging honesty/completeness has to match the note to the
 * photo. Deliberately a LABEL, not an SVG map: the admin decides whether
 * the report is honest and complete, and the note plus the photo answer
 * that — a second implementation of the map geometry in web would double
 * the most fragile part of the feature for zero extra operator decisions
 * (see `docs/plans/DAMAGE-MAP-PLAN.md` §8).
 *
 * Empty state (`defects.length === 0`) — a muted single line, matching
 * the `MediaTab` empty pattern.
 *
 * Severity colours come from the theme palette via `<DefectSeverityBadge>`
 * (no raw hex here — CLAUDE.md rule #1). All copy is i18n (rule #11).
 */
export interface DefectsSectionProps {
  defects: AdminReportDefectDto[];
}

const SectionTitle = ({ count }: { count: number }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.6px',
        color: tokens.text.tertiary,
        mb: 1.25,
        display: 'flex',
        alignItems: 'baseline',
        gap: 0.75,
      }}
    >
      {t('reports.defects.sectionTitle')}
      {count > 0 ? (
        <Box
          component="span"
          sx={{ fontFamily: 'monospace', color: tokens.text.secondary }}
          data-testid="report-detail-defects-count"
        >
          {count}
        </Box>
      ) : null}
    </Typography>
  );
};

/**
 * The body zone the defect sits on, or `null` when it has none.
 *
 * Two rules live here and both are load-bearing:
 *
 * 1. **No zone ⇒ NO element.** Not a dash, not an empty chip, not «зона не
 *    указана». `zone` is nullable by design (a mechanical finding has no
 *    place on the body, and there is no backfill — every pre-AUT-336 row is
 *    NULL), so a placeholder would put visual noise in literally every
 *    existing defect row and buy the operator nothing.
 * 2. **An unknown zone renders VERBATIM.** `DEFECT_ZONES` is append-only,
 *    but a zone can be retired from the map geometry while old rows keep
 *    carrying it — and the wire type is `string | null` precisely because
 *    the read path is tolerant. `defaultValue` hands the raw id to the
 *    operator (readable, obviously machine-ish) instead of rendering a
 *    missing-key path or throwing.
 */
const ZoneChip = ({ zone }: { zone: string | null }): ReactElement | null => {
  const { t } = useTranslation();
  if (zone === null) {
    return null;
  }
  return (
    <Chip
      size="small"
      variant="outlined"
      icon={<PlaceOutlinedIcon />}
      label={t(`reports.defects.zone.${zone}`, { defaultValue: zone })}
      title={t('reports.defects.zoneHint')}
      data-testid="defect-zone"
    />
  );
};

/** A single defect row inside the card. */
const DefectRow = ({ defect }: { defect: AdminReportDefectDto }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        p: '12px 14px',
        '& + &': { borderTop: '1px solid', borderColor: 'divider' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <DefectSeverityBadge severity={defect.severity} />
        <Typography sx={{ fontSize: 11, fontWeight: 500, color: tokens.text.tertiary }}>
          {t(`reports.defects.category.${defect.category}`, { defaultValue: defect.category })}
        </Typography>
        <ZoneChip zone={defect.zone} />
        {defect.mediaId !== null ? (
          <Chip
            size="small"
            variant="outlined"
            icon={<PhotoCameraOutlinedIcon />}
            label={t('reports.defects.mediaCue')}
            title={t('reports.defects.mediaCueHint')}
            sx={{ ml: 'auto' }}
          />
        ) : null}
      </Box>
      <Typography sx={{ fontSize: 14, fontWeight: 500, color: tokens.text.primary }}>
        {defect.note}
      </Typography>
    </Box>
  );
};

export const DefectsSection = ({ defects }: DefectsSectionProps): ReactElement => {
  const { t } = useTranslation();

  if (defects.length === 0) {
    return (
      <Box>
        <SectionTitle count={0} />
        <Typography variant="body2" sx={{ color: tokens.text.tertiary }}>
          {t('reports.defects.empty')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <SectionTitle count={defects.length} />
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: `${tokens.radius.card}px`,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        {defects.map((defect) => (
          <DefectRow key={defect.id} defect={defect} />
        ))}
      </Box>
    </Box>
  );
};
